export const SNAPSHOT_PANEL_MIN_WIDTH_PX = 320
export const SNAPSHOT_PANEL_MAX_WIDTH_PX = 1000
export const SNAPSHOT_PANEL_MIN_HEIGHT_PX = 320

export type FloatingPanelRect = {
    left: number
    top: number
    width: number
    height: number
}

export function clampFloatingPanelRect(rect: FloatingPanelRect): FloatingPanelRect {
    const margin = 8
    const maxW = window.innerWidth - margin * 2
    const maxH = window.innerHeight - margin * 2
    const width = Math.min(
        SNAPSHOT_PANEL_MAX_WIDTH_PX,
        maxW,
        Math.max(SNAPSHOT_PANEL_MIN_WIDTH_PX, rect.width)
    )
    const height = Math.min(maxH, Math.max(SNAPSHOT_PANEL_MIN_HEIGHT_PX, rect.height))
    const left = Math.min(
        Math.max(margin, rect.left),
        window.innerWidth - width - margin
    )
    const top = Math.min(
        Math.max(margin, rect.top),
        window.innerHeight - height - margin
    )
    return { left, top, width, height }
}

export function clampPanelWidth(width: number, minMapWidth = 200): number {
    const margin = 8
    const maxW = Math.min(
        SNAPSHOT_PANEL_MAX_WIDTH_PX,
        window.innerWidth - margin * 2 - minMapWidth
    )
    return Math.max(SNAPSHOT_PANEL_MIN_WIDTH_PX, Math.min(maxW, width))
}

export function defaultFloatingPanelRect(width: number): FloatingPanelRect {
    return clampFloatingPanelRect({
        left: Math.max(16, window.innerWidth - width - 300),
        top: 72,
        width,
        height: Math.min(780, window.innerHeight - 88),
    })
}

type RectUpdater = (rect: FloatingPanelRect) => void

function bindPointerSession(
    move: (event: PointerEvent) => void,
    pointerId: number,
    target?: HTMLElement | null
) {
    target?.setPointerCapture?.(pointerId)
    const up = () => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", up)
        window.removeEventListener("pointercancel", up)
        if (target?.hasPointerCapture?.(pointerId)) {
            try {
                target.releasePointerCapture(pointerId)
            } catch {
                // ignore
            }
        }
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", up)
}

export function startPanelResizeWest(
    event: React.PointerEvent,
    width: number,
    onWidthChange: (width: number) => void
) {
    event.preventDefault()
    event.stopPropagation()
    const startX = event.clientX
    const baseW = width

    bindPointerSession((ev) => {
        onWidthChange(clampPanelWidth(baseW + (startX - ev.clientX)))
    }, event.pointerId, event.currentTarget instanceof HTMLElement ? event.currentTarget : null)
}

export function startPanelResizeEast(
    event: React.PointerEvent,
    rect: FloatingPanelRect,
    onRectChange: RectUpdater
) {
    event.preventDefault()
    event.stopPropagation()
    const startX = event.clientX
    const base = { ...rect }

    bindPointerSession((ev) => {
        onRectChange(clampFloatingPanelRect({
            ...base,
            width: base.width + (ev.clientX - startX),
        }))
    }, event.pointerId, event.currentTarget instanceof HTMLElement ? event.currentTarget : null)
}

export function startPanelResizeSouthEast(
    event: React.PointerEvent,
    rect: FloatingPanelRect,
    onRectChange: RectUpdater
) {
    event.preventDefault()
    event.stopPropagation()
    const startX = event.clientX
    const startY = event.clientY
    const base = { ...rect }

    bindPointerSession((ev) => {
        onRectChange(clampFloatingPanelRect({
            ...base,
            width: base.width + (ev.clientX - startX),
            height: base.height + (ev.clientY - startY),
        }))
    }, event.pointerId, event.currentTarget instanceof HTMLElement ? event.currentTarget : null)
}
